import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setLoading(false);
      return;
    } catch (err) {
      const status = err?.response?.status;
      // Transient error (network / 5xx): DON'T log out — retry once, keep session.
      if (status !== 401 && status !== 403) {
        try {
          await new Promise((r) => setTimeout(r, 1500));
          const { data } = await api.get("/auth/me");
          setUser(data);
          setLoading(false);
          return;
        } catch (e2) {
          const s2 = e2?.response?.status;
          if (s2 !== 401 && s2 !== 403) {
            setUser(false);
            setLoading(false);
            return;
          }
        }
      }
    }
    // A real auth failure: try a silent refresh (httpOnly cookie) before giving up.
    try {
      const { data } = await api.post("/auth/refresh");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The axios interceptor fires this when a token refresh definitively fails.
  useEffect(() => {
    const onForcedLogout = () => setUser(false);
    window.addEventListener("nox-auth-logout", onForcedLogout);
    return () => window.removeEventListener("nox-auth-logout", onForcedLogout);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password) => {
    const { data } = await api.post("/auth/register", { email, password });
    try { sessionStorage.setItem("nox_just_signed_up", "1"); } catch {}
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  const setCredits = useCallback((credits) => {
    setUser((prev) => (prev ? { ...prev, credits } : prev));
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const { data } = await api.get("/credits");
      setCredits(data.credits);
    } catch {}
  }, [setCredits]);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, refresh, setCredits, refreshCredits }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
